import {
  AxesBarsOptions,
  AxisOptions,
  GraphDimensions,
  GraphMargins,
  xAxisTick,
  yAxisTick,
} from "types/graph";
import { opacity } from "utils/canvas";
import { Color } from "utils/theme";
import { polyModelTransform } from "./fuel-curve-utils";

/**
 * Draws a frame around the graph.
 *
 * @param ctx - The canvas rendering context to draw on.
 * @param graphDimensions - The dimensions of the graph to draw the frame around.
 */
export const drawFrame = (
  ctx: CanvasRenderingContext2D,
  graphDimensions: GraphDimensions
) => {
  ctx.strokeStyle = Color.Primary[600];
  ctx.lineWidth = 1;
  ctx.strokeRect(
    graphDimensions.left,
    graphDimensions.top,
    graphDimensions.width,
    graphDimensions.height
  );
};

/**
 * Draws grid lines on a canvas context based on the provided axis ticks and graph dimensions.
 *
 * @param ctx - The canvas rendering context where the grid lines will be drawn.
 * @param xAxisTicks - An array of tick objects for the X-axis.
 * @param yAxisTicks - An array of tick objects for the Y-axis.
 * @param graphDimensions - An object containing the dimensions of the graph.
 */
export const drawGrid = (
  ctx: CanvasRenderingContext2D,
  xAxisTicks: xAxisTick[],
  yAxisTicks: yAxisTick[],
  graphDimensions: GraphDimensions
) => {
  // Draw gridlines
  ctx.strokeStyle = Color.Primary[700];
  ctx.lineWidth = 1;

  for (const tick of xAxisTicks) {
    if (!tick.major) {
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(tick.x, graphDimensions.top);
    ctx.lineTo(tick.x, graphDimensions.bottom);
    ctx.stroke();
  }
  for (const tick of yAxisTicks) {
    if (!tick.major) {
      continue;
    }
    ctx.beginPath();
    ctx.moveTo(graphDimensions.left, tick.y);
    ctx.lineTo(graphDimensions.right, tick.y);
    ctx.stroke();
  }
};

/**
 * Calculates the ticks for the X-axis of a graph.
 *
 * @param minValue - The minimum value on the X-axis.
 * @param maxValue - The maximum value on the X-axis.
 * @param convertFn - A function to convert the tick value to the desired format.
 * @returns An object containing an array of X-axis ticks.
 */
export const calculateXAxisTicks = (
  minValue: number,
  maxValue: number,
  convertFn: (value: number) => number
) => {
  const xAxisTicks: xAxisTick[] = [];
  const startTick = Math.floor(minValue);

  for (let i = startTick; i <= maxValue; i++) {
    xAxisTicks.push({
      x: convertFn(i),
      sog: i,
      major: i % 5 === 0,
    });
  }

  return { xAxisTicks };
};

/**
 * Calculates the Y-axis ticks for a fuel curve graph.
 *
 * @param minValue - The minimum value of the Y-axis.
 * @param maxValue - The maximum value of the Y-axis.
 * @param graphDimensions - The dimensions of the graph.
 * @param convertFn - A function to convert a fuel rate value to a Y-coordinate.
 * @returns An object containing the Y-axis ticks and a flag indicating if fractional ticks exist.
 */
export const calculateYAxisTicks = (
  minValue: number,
  maxValue: number,
  graphDimensions: GraphDimensions,
  convertFn: (value: number) => number
): { yAxisTicks: yAxisTick[]; hasFractionalTicks: boolean } => {
  const yAxisTicks: yAxisTick[] = [];
  const range = maxValue - minValue;
  const startTickFuelRate = Math.floor(minValue); // Round down to nearest integer
  const yAxisLabelMonoval = Math.max(5, Math.round(range / 5) * 5); // Adjust label monoval to nearest 5
  const tickDistanceThreshold = 5; // Minimum distance between minor ticks
  const tickDist = graphDimensions.height / range / 10; // Distance between ticks in pixels
  let hasFractionalTicks = false; // Flag to check if fractional ticks exist

  // Loop through fuel rates scaled by 10 (to check for fractions easily)
  for (let i = startTickFuelRate * 10; i <= maxValue * 10; i++) {
    const fuelRate = i / 10; // Scale back to the actual fuel rate
    const isMajorTick = i % yAxisLabelMonoval === 0; // Determine if it's a major tick

    // Skip minor ticks if they are too close together
    if (!isMajorTick && tickDist < tickDistanceThreshold) {
      continue;
    }

    const y = convertFn(fuelRate); // Convert fuel rate to y-coordinate

    // Skip ticks that are beyond the graph's bottom boundary
    if (y > graphDimensions.bottom) {
      continue;
    }

    // Check if this tick is fractional (e.g., not ending in .0)
    if (!fuelRate.toFixed(1).endsWith(".0")) {
      hasFractionalTicks = true;
    }

    // Add the tick to the axisY array
    yAxisTicks.push({
      y,
      fuelRate,
      major: isMajorTick,
    });
  }

  return { yAxisTicks, hasFractionalTicks };
};

/**
 * Draws a series of ticks using the provided drawing function.
 *
 * @template T - The type of the ticks.
 * @param {T[]} ticks - An array of ticks to be drawn.
 * @param {(tick: T) => void} drawFn - A function that draws a single tick.
 */
const drawTicks = <T>(ticks: T[], drawFn: (tick: T) => void) => {
  ticks.forEach(drawFn);
};

/**
 * Draws the axes on a canvas context with specified ticks and options.
 *
 * @param ctx - The canvas rendering context where the axes will be drawn.
 * @param xAxisTicks - An array of ticks for the x-axis.
 * @param yAxisTicks - An array of ticks for the y-axis.
 * @param graphDimensions - The dimensions of the graph.
 * @param options - Configuration options for the axes.
 */
export const drawAxes = (
  ctx: CanvasRenderingContext2D,
  xAxisTicks: xAxisTick[],
  yAxisTicks: yAxisTick[],
  graphDimensions: GraphDimensions,
  options: AxisOptions
) => {
  drawTicks<xAxisTick>(xAxisTicks, (tick: xAxisTick) => {
    if (!tick.major) {
      return;
    }
    ctx.lineWidth = 1;
    ctx.font = `400 ${options.fontSize * 0.8}px "Mono", sans-serif`;
    ctx.fillStyle = Color.Primary[400];
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.strokeStyle = Color.Primary[600];
    ctx.beginPath();
    ctx.moveTo(tick.x, graphDimensions.bottom + 1);
    ctx.lineTo(tick.x, graphDimensions.bottom + 8);
    ctx.stroke();

    // Hide the label instead of letting the canvas edge clip it
    const label = "" + tick.sog;
    const halfLabelWidth = ctx.measureText(label).width / 2;
    if (
      tick.x - halfLabelWidth >= 0 &&
      tick.x + halfLabelWidth <= ctx.canvas.width
    ) {
      ctx.fillText(label, tick.x, graphDimensions.bottom + 25);
    }
  });

  const fuelRateAccuracy =
    options.yAxis.hasFractionalTicks && options.yAxis.range < 30 ? 1 : 0;

  drawTicks<yAxisTick>(yAxisTicks, (tick) => {
    if (!tick.major) {
      return;
    }
    ctx.lineWidth = 1;
    ctx.font = `400 ${options.fontSize * 0.8}px "Mono", sans-serif`;
    ctx.fillStyle = Color.Primary[400];
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.strokeStyle = Color.Primary[600];
    ctx.beginPath();
    ctx.moveTo(graphDimensions.left, tick.y);
    ctx.lineTo(graphDimensions.left - 8, tick.y);
    ctx.stroke();

    // Hide the label instead of letting the canvas edge clip it
    const halfLabelHeight = (options.fontSize * 0.8) / 2;
    if (
      tick.y - halfLabelHeight >= 0 &&
      tick.y + halfLabelHeight <= ctx.canvas.height
    ) {
      ctx.fillText(
        tick.fuelRate.toFixed(fuelRateAccuracy),
        graphDimensions.left - 25,
        tick.y
      );
    }
  });
};

/**
 * Draws the axes bars on a canvas context.
 *
 * @param ctx - The canvas rendering context.
 * @param xValue - The x-coordinate value where the x-axis bar should be drawn.
 * @param yValue - The y-coordinate value where the y-axis bar should be drawn.
 * @param graphDimensions - The dimensions of the graph.
 * @param margins - The margins of the graph.
 * @param options - The options for drawing the axes bars, including colors.
 */
export const drawAxesBars = (
  ctx: CanvasRenderingContext2D,
  xValue: number,
  yValue: number,
  graphDimensions: GraphDimensions,
  margins: GraphMargins,
  options: AxesBarsOptions
) => {
  const xAxisBarWidth = margins.bottom;
  const yAxisBarWidth = 65;

  if (xValue > graphDimensions.left) {
    // Draw x-axis bar
    ctx.globalAlpha = 0.2;
    ctx.beginPath();
    ctx.moveTo(graphDimensions.left, graphDimensions.bottom);
    ctx.lineTo(xValue, graphDimensions.bottom);
    ctx.lineTo(xValue, graphDimensions.bottom + xAxisBarWidth);
    ctx.lineTo(
      graphDimensions.left - yAxisBarWidth,
      graphDimensions.bottom + xAxisBarWidth
    );
    ctx.closePath();
    ctx.fillStyle = options.xAxis.color;
    ctx.fill();
    ctx.globalAlpha = 1.0;

    // Draw x-axis triangle
    ctx.fillStyle = opacity(options.xAxis.color, 0.7);
    ctx.beginPath();
    ctx.moveTo(xValue, graphDimensions.bottom);
    ctx.lineTo(
      xValue - margins.bottom / 1.5,
      graphDimensions.bottom + margins.bottom
    );
    ctx.lineTo(
      xValue + margins.bottom / 1.5,
      graphDimensions.bottom + margins.bottom
    );
    ctx.fill();
  }

  // Draw y-axis bar
  if (yValue < graphDimensions.bottom) {
    ctx.globalAlpha = 0.2;
    ctx.lineWidth = yAxisBarWidth;
    ctx.strokeStyle = options.yAxis.color;
    ctx.beginPath();
    ctx.moveTo(graphDimensions.left, graphDimensions.bottom);
    ctx.lineTo(graphDimensions.left, yValue);
    ctx.lineTo(graphDimensions.left - yAxisBarWidth, yValue);
    ctx.lineTo(
      graphDimensions.left - yAxisBarWidth,
      graphDimensions.bottom + xAxisBarWidth
    );
    ctx.closePath();
    ctx.fillStyle = options.yAxis.color;
    ctx.fill();
    ctx.globalAlpha = 1;

    // Draw y-axis triangle
    ctx.fillStyle = opacity(options.yAxis.color, 0.7);
    ctx.beginPath();
    ctx.moveTo(graphDimensions.left - 0, yValue);
    ctx.lineTo(
      graphDimensions.left - yAxisBarWidth,
      yValue - yAxisBarWidth / 2
    );
    ctx.lineTo(
      graphDimensions.left - yAxisBarWidth,
      yValue + yAxisBarWidth / 2
    );
    ctx.fill();
  }
};

/**
 * Draws a clipping mask on the provided canvas context.
 *
 * @param ctx - The canvas rendering context on which to draw the clipping mask.
 * @param left - The left coordinate of the clipping mask.
 * @param right - The right coordinate of the clipping mask.
 * @param top - The top coordinate of the clipping mask.
 * @param bottom - The bottom coordinate of the clipping mask.
 */
export const drawClippingMask = (
  ctx: CanvasRenderingContext2D,
  left: number,
  right: number,
  top: number,
  bottom: number
) => {
  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, right - left, bottom - top);
  ctx.clip();
};

/**
 * Draws a filled area on a canvas representing the fuel curve.
 *
 * @param ctx - The 2D rendering context for the drawing surface of the canvas element.
 * @param data - The data object containing the fuel curve information.
 * @param color - The color to fill the area with.
 * @param convertX - A function to convert the x-coordinate value.
 * @param convertY - A function to convert the y-coordinate value.
 */
export const drawFuelCurveArea = (
  ctx: CanvasRenderingContext2D,
  data: any,
  color: string,
  convertX: (value: number) => number,
  convertY: (value: number) => number
) => {
  if (!data && !data?.smoothFuelGhostLower && !data?.smoothFuelGhostUpper)
    return;

  const { smoothFuelGhostLower, smoothFuelGhostUpper } = data;

  ctx.fillStyle = color;
  ctx.beginPath();

  for (let i = 0; i < smoothFuelGhostUpper.length; i++) {
    if (i === 0) {
      ctx.moveTo(
        convertX(smoothFuelGhostUpper[i].x),
        convertY(smoothFuelGhostUpper[i].y)
      );
    } else {
      ctx.lineTo(
        convertX(smoothFuelGhostUpper[i].x),
        convertY(smoothFuelGhostUpper[i].y)
      );
    }
  }

  for (let i = smoothFuelGhostLower.length - 1; i >= 0; i--) {
    ctx.lineTo(
      convertX(smoothFuelGhostLower[i].x),
      convertY(smoothFuelGhostLower[i].y)
    );
  }
  ctx.fill();
};

/**
 * Draws the fuel curve on a given canvas context.
 *
 * @param ctx - The canvas rendering context where the fuel curve will be drawn.
 * @param data - The data object containing fuel curve model coefficients and statistics.
 * @param color - The color of the fuel curve line.
 * @param lineWidth - The width of the fuel curve line.
 * @param maxSogGraph - The maximum speed over ground (SOG) value for the graph.
 * @param convertX - A function to convert SOG values to canvas X coordinates.
 * @param convertY - A function to convert fuel rate values to canvas Y coordinates.
 * @param graphDimensions - The dimensions of the graph, used for the area fill.
 * @param safetySpeed - The safety speed; the curve beyond it is drawn in dangerColor.
 * @param dangerColor - The color for the part of the curve beyond the safety speed.
 * @param goodColor - The color for the parts of the curve inside the eco ranges.
 *                    The danger color takes precedence where they overlap.
 */
export const drawFuelCurve = (
  ctx: CanvasRenderingContext2D,
  data: any,
  color: string,
  lineWidth: number,
  maxSogGraph: number,
  convertX: (value: number) => number,
  convertY: (value: number) => number,
  graphDimensions: GraphDimensions,
  safetySpeed?: number | null,
  dangerColor?: string,
  goodColor?: string
) => {
  const polyModel = {
    base: data?.fuel_curve_model_coef,
    corrected: data?.fuel_curve_model_corrected_coef,
  };

  let sog_range: number[] = [];
  if (
    data?.fuel_curve_stats?.stable_fuel_curve_sog_range &&
    data.fuel_curve_stats.stable_fuel_curve_sog_range[1]
  ) {
    sog_range = data?.fuel_curve_stats?.stable_fuel_curve_sog_range;
  } else if (
    data?.fuel_curve_stats?.outlier_free_range &&
    data.fuel_curve_stats.outlier_free_range[1]
  ) {
    // This is a fallback for the case where the stable fuel curve range is not available
    // Should only apply for vessels with very limited data.
    sog_range = data?.fuel_curve_stats?.outlier_free_range.slice();
    if (sog_range[1]) {
      sog_range[1] = Math.min(sog_range[1], maxSogGraph) * 0.95;
    }
  }

  if (polyModel.corrected && sog_range) {
    // Draw poly model (fuel curve)
    const polyModelPath: any[] = [];
    for (let sog = sog_range[0]; sog < sog_range[1]; sog += 0.15) {
      const fuelRate = polyModelTransform([sog], polyModel.corrected)[0];
      polyModelPath.push({
        x: convertX(sog),
        y: convertY(fuelRate),
        sog: sog,
      });
    }

    if (polyModelPath.length < 2) return;

    // Resolve the curve color at a given SOG: danger beyond the safety speed,
    // good inside eco ranges (danger wins where they overlap), base otherwise.
    const colorForSog = (sog: number) => {
      if (
        dangerColor &&
        safetySpeed !== null &&
        safetySpeed !== undefined &&
        sog > safetySpeed
      ) {
        return dangerColor;
      }
      if (
        goodColor &&
        data?.eco_ranges?.some(
          (range: { start: number; end: number }) =>
            sog >= range.start && sog <= range.end
        )
      ) {
        return goodColor;
      }
      return color;
    };

    // Split a run of points into same-colored segments. Each segment starts
    // with the previous segment's last point so the segments connect.
    const splitByColor = (points: any[]) => {
      const segments: { points: any[]; segmentColor: string }[] = [];
      for (const p of points) {
        const segmentColor = colorForSog(p.sog);
        const current = segments[segments.length - 1];
        if (!current || current.segmentColor !== segmentColor) {
          const prev = current?.points[current.points.length - 1];
          segments.push({
            points: prev ? [prev, p] : [p],
            segmentColor,
          });
        } else {
          current.points.push(p);
        }
      }
      return segments;
    };

    // Faded area fill under the curve. The fade is anchored to the whole
    // curve's top so it stays continuous across the color boundary.
    const areaTopY = Math.min(...polyModelPath.map((p) => p.y));
    const fillFadedArea = (points: any[], fillColor: string) => {
      if (points.length < 2) return;
      const gradient = ctx.createLinearGradient(
        0,
        areaTopY,
        0,
        graphDimensions.bottom
      );
      gradient.addColorStop(0, opacity(fillColor, 0.3));
      gradient.addColorStop(1, opacity(fillColor, 0));
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(points[0].x, graphDimensions.bottom);
      for (const p of points) {
        ctx.lineTo(p.x, p.y);
      }
      ctx.lineTo(points[points.length - 1].x, graphDimensions.bottom);
      ctx.closePath();
      ctx.fill();
    };

    const strokeCurve = (
      points: any[],
      segmentColor: string,
      dashed: boolean
    ) => {
      if (points.length < 2) return;
      ctx.strokeStyle = dashed ? opacity(segmentColor, 0.5) : segmentColor;
      ctx.setLineDash(dashed ? [8, 2] : []);
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (const p of points.slice(1)) {
        ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
    };

    for (const segment of splitByColor(polyModelPath)) {
      fillFadedArea(segment.points, segment.segmentColor);
    }

    // The section above the current max obtainable SOG is drawn dashed
    const maxSogIdx = data?.current_max_sog
      ? polyModelPath.findIndex((p) => p.sog > data.current_max_sog)
      : -1;
    const solidPoints =
      maxSogIdx === -1 ? polyModelPath : polyModelPath.slice(0, maxSogIdx);
    const dashedPoints =
      maxSogIdx === -1 ? [] : polyModelPath.slice(Math.max(maxSogIdx - 1, 0));

    ctx.lineCap = "round";
    ctx.lineWidth = lineWidth;
    for (const segment of splitByColor(solidPoints)) {
      strokeCurve(segment.points, segment.segmentColor, false);
    }
    for (const segment of splitByColor(dashedPoints)) {
      strokeCurve(segment.points, segment.segmentColor, true);
    }
    ctx.lineCap = "butt";
  }
};

/**
 * Draws eco ranges on a canvas context.
 *
 * @param ctx - The canvas rendering context to draw on.
 * @param data - The data containing eco ranges with start and end values.
 * @param convertX - A function to convert a value to the x-coordinate on the canvas.
 * @param color - The color to fill the eco ranges.
 * @param graphDimensions - The dimensions of the graph.
 * @param safetySpeed - If set, the ranges are not drawn past this speed.
 */
export const drawEcoRanges = (
  ctx: CanvasRenderingContext2D,
  data: any,
  convertX: (value: number) => number,
  color: string,
  graphDimensions: GraphDimensions,
  safetySpeed?: number | null
) => {
  if (!data || !data.length) return;

  data.forEach((ecoRange: { start: number; end: number }) => {
    const hasSafetySpeed = safetySpeed !== null && safetySpeed !== undefined;
    if (hasSafetySpeed && ecoRange.start >= safetySpeed) return;

    const startX = convertX(ecoRange.start);
    const endX = convertX(
      hasSafetySpeed ? Math.min(ecoRange.end, safetySpeed) : ecoRange.end
    );
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(startX, graphDimensions.top);
    ctx.lineTo(startX, graphDimensions.bottom);
    ctx.lineTo(endX, graphDimensions.bottom);
    ctx.lineTo(endX, graphDimensions.top);
    ctx.closePath();
    ctx.fill();
  });
};

/**
 * Draws an eco line on the given canvas context.
 *
 * @param ctx - The canvas rendering context to draw on.
 * @param ecoSogX - The x-coordinate for the eco-SOG line.
 * @param topY - The y-coordinate the line extends up to.
 * @param label - The label to display for the eco-SOG line.
 * @param color - The color of the eco-SOG line.
 * @param graphDimensions - The dimensions of the graph.
 * @param options - Additional options for drawing, including font size and label visibility.
 */
export const drawEcoLine = (
  ctx: CanvasRenderingContext2D,
  ecoSogX: number,
  topY: number,
  label: string,
  color: string,
  graphDimensions: GraphDimensions,
  options: { fontSize: number; showLabel?: boolean }
) => {
  let offset = 0;

  if (options.showLabel !== false) {
    // Eco-SOG label
    ctx.save();
    ctx.translate(ecoSogX, graphDimensions.bottom - 10);
    ctx.rotate(-Math.PI / 2);
    ctx.font = `500 ${options.fontSize}px "Mono", sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "white";
    ctx.fillText(label, 0, 0);
    const ecoLabelBox = ctx.measureText(label);
    ctx.restore();

    offset = ecoLabelBox.width + 15;
  }

  // Eco-SOG line
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.setLineDash([10, 5]);
  ctx.beginPath();
  ctx.moveTo(ecoSogX, graphDimensions.bottom - offset);
  ctx.lineTo(ecoSogX, topY);
  ctx.stroke();
  ctx.setLineDash([]);
};

/**
 * Draws a safety line and its label on a canvas context.
 *
 * @param ctx - The canvas rendering context.
 * @param safetySpeedX - The x-coordinate for the safety line.
 * @param label - The label text for the safety line.
 * @param color - The color of the safety line.
 * @param graphDimensions - The dimensions of the graph.
 * @param options - Additional options for drawing, including font size and label visibility.
 */
export const drawSafetyLine = (
  ctx: CanvasRenderingContext2D,
  safetySpeedX: number,
  label: string,
  color: string,
  graphDimensions: GraphDimensions,
  options: { fontSize: number; showLabel?: boolean }
) => {
  let offset = 0;

  if (options.showLabel !== false) {
    // Safety speed label
    ctx.save();
    ctx.translate(safetySpeedX, graphDimensions.bottom - 10);
    ctx.rotate(-Math.PI / 2);
    ctx.font = `500 ${options.fontSize}px "Mono", sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "white";
    ctx.fillText(label, 0, 0);
    const safetyLabelBox = ctx.measureText(label);
    ctx.restore();

    offset = safetyLabelBox.width + 15;
  }

  // Safety speed line
  ctx.strokeStyle = opacity(color, 0.6);
  ctx.lineWidth = 3;
  ctx.setLineDash([10, 5]);
  ctx.beginPath();
  ctx.moveTo(safetySpeedX, graphDimensions.bottom - offset);
  ctx.lineTo(safetySpeedX, graphDimensions.top);
  ctx.stroke();
  ctx.setLineDash([]);
};

/**
 * Draws the danger zone on the fuel curve graph.
 *
 * @param ctx - The canvas rendering context.
 * @param graphDimensions - The dimensions of the graph.
 * @param color - The color to use for the danger zone.
 * @param maxSogGraph - The maximum speed over ground (SOG) value on the graph.
 * @param fuelRateWarnThreshold - The fuel rate warning threshold.
 * @param fuelRateToY - Function to convert fuel rate to Y coordinate.
 * @param sogToX - Function to convert SOG to X coordinate.
 * @param safetySpeed - The safety speed limit.
 * @param maxFuelRateGraph - The maximum fuel rate value on the graph.
 * @param safetyLimitLabel - The label for the safety limit.
 * @param axisTextSizePx - The size of the axis text in pixels.
 * @param hasTripStarted - Indicates if the trip has started.
 * @param shadeUnsafeSpeed - Indicates if the unsafe speed region should be shaded.
 * @param exceedingLimits - Indicates if the limits are being exceeded.
 * @param showSafetyLabel - Indicates if the safety limit label should be drawn.
 */
export const drawDangerZone = (
  ctx: CanvasRenderingContext2D,
  graphDimensions: GraphDimensions,
  color: string,
  maxSogGraph: number,
  fuelRateWarnThreshold: number | null | undefined,
  fuelRateToY: (value: number) => number,
  sogToX: (value: number) => number,
  safetySpeed: number | null,
  maxFuelRateGraph: number,
  safetyLimitLabel: string,
  axisTextSizePx: number,
  hasTripStarted: boolean,
  shadeUnsafeSpeed: boolean,
  exceedingLimits: boolean,
  showSafetyLabel = true
) => {
  const showSafetyLimit = safetySpeed && safetySpeed < maxSogGraph;
  const showFuelRateLimit =
    fuelRateWarnThreshold && fuelRateWarnThreshold < maxFuelRateGraph;
  const safetyRegionX =
    shadeUnsafeSpeed && showSafetyLimit
      ? sogToX(safetySpeed)
      : graphDimensions.right;

  if (hasTripStarted && (showSafetyLimit || showFuelRateLimit)) {
    const fuelRateLimitY = fuelRateWarnThreshold
      ? fuelRateToY(fuelRateWarnThreshold)
      : graphDimensions.top;

    ctx.fillStyle = opacity(color, exceedingLimits ? 0.25 : 0.1);
    ctx.beginPath();
    ctx.moveTo(safetyRegionX, graphDimensions.bottom);
    ctx.lineTo(graphDimensions.right, graphDimensions.bottom);
    ctx.lineTo(graphDimensions.right, graphDimensions.top);
    ctx.lineTo(graphDimensions.left, graphDimensions.top);
    ctx.lineTo(graphDimensions.left, fuelRateLimitY);
    ctx.lineTo(safetyRegionX, fuelRateLimitY);
    ctx.closePath();
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(safetyRegionX, graphDimensions.bottom);
    ctx.lineTo(safetyRegionX, fuelRateLimitY);
    ctx.lineTo(graphDimensions.left, fuelRateLimitY);
    ctx.strokeStyle = opacity(color, 0.4);
    ctx.lineWidth = 1;
    ctx.stroke();

    // Add labels
    ctx.font = `500 ${axisTextSizePx * 0.9}px "Mono", sans-serif`;
    if (showSafetyLimit && shadeUnsafeSpeed && showSafetyLabel) {
      ctx.save();
      ctx.translate(safetyRegionX + 10, graphDimensions.bottom - 10);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = color;
      ctx.fillText(safetyLimitLabel, 0, 0);
      ctx.restore();
    }
  }
};

/**
 * Draws a target fuel rate line on a canvas context.
 *
 * @param ctx - The canvas rendering context to draw on.
 * @param label - The label to display on the target fuel rate line.
 * @param graphDimensions - The dimensions of the graph.
 * @param safetySpeedX - The x-coordinate of the safety speed line.
 * @param ecoSogX - The x-coordinate of the eco speed over ground line.
 * @param clipRegionX - The x-coordinate where the line should be clipped.
 * @param maxFuelRateY - The y-coordinate of the maximum fuel rate.
 */
export const drawTargetFuelRateLine = (
  ctx: CanvasRenderingContext2D,
  label: string,
  graphDimensions: GraphDimensions,
  safetySpeedX: number,
  ecoSogX: number,
  clipRegionX: number,
  maxFuelRateY: number
) => {
  // Draw target fuel rate line
  ctx.beginPath();
  ctx.moveTo(graphDimensions.left, maxFuelRateY);
  ctx.lineTo(clipRegionX, maxFuelRateY);
  ctx.strokeStyle = opacity(Color.White, 0.8);
  ctx.lineWidth = 2;
  ctx.setLineDash([10, 5]);
  ctx.stroke();
  ctx.setLineDash([]);

  // Label
  const targetRateLabelBox = ctx.measureText(label);
  const margin = 10;
  const overlapsSafetyLine =
    safetySpeedX &&
    targetRateLabelBox.width + margin >= safetySpeedX - graphDimensions.left;
  const overlapsEcoLine =
    ecoSogX &&
    targetRateLabelBox.width + margin >= ecoSogX - graphDimensions.left;
  const showLabelOnRight = overlapsSafetyLine || overlapsEcoLine;
  const labelX = showLabelOnRight
    ? graphDimensions.right - margin
    : graphDimensions.left + margin;
  ctx.textAlign = showLabelOnRight ? "right" : "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = Color.White;
  ctx.fillText(label, labelX, maxFuelRateY + 6);
};

/**
 * Draws the axes lines and current fuel rate point on the graph.
 *
 * @param ctx - The canvas rendering context.
 * @param graphDimensions - The dimensions of the graph.
 * @param sogX - The x-coordinate for the Speed Over Ground (SOG) line.
 * @param fuelRateY - The y-coordinate for the fuel rate line.
 * @param exceedingLimits - A boolean indicating if the current fuel rate exceeds limits.
 * @param isInEcoZone - A boolean indicating if the vessel is in the eco zone (and safe).
 * @param graphColors - An object containing the colors used for the graph.
 */
export const drawAxesLines = (
  ctx: CanvasRenderingContext2D,
  graphDimensions: GraphDimensions,
  sogX: number,
  fuelRateY: number,
  exceedingLimits: boolean,
  isInEcoZone: boolean,
  graphColors: any
) => {
  const lineColor = exceedingLimits
    ? graphColors.Bad
    : isInEcoZone
    ? graphColors.Good
    : Color.Primary[400];

  // Draw current fuel rate line
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 2.5;
  ctx.setLineDash([10, 5]);
  ctx.beginPath();
  ctx.moveTo(graphDimensions.left, fuelRateY);
  ctx.lineTo(sogX, fuelRateY);
  ctx.stroke();

  // Draw current SOG line
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(sogX, graphDimensions.bottom);
  ctx.lineTo(sogX, fuelRateY);
  ctx.stroke();
  ctx.setLineDash([]);

  // Draw current fuel rate point
  ctx.strokeStyle = Color.White;
  ctx.fillStyle = exceedingLimits
    ? graphColors.Bad
    : isInEcoZone
    ? graphColors.Good
    : Color.Primary[200];
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(sogX, fuelRateY, 7, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
};
